import withStore, { TLoadingAwarePresenter, TPresentable } from "../withStore";
import Application from "../../Application";
import ReportingPeriod, {
  IReportingPeriod
} from "../../model/reporting/ReportingPeriod";
import ShowErrorMessageInteractor from "../../interactor/notifications/ShowErrorMessageInteractor";
import ReportingPeriodPresentationModel, {
  TReportingPeriodPresentationModel
} from "./model/ReportingPeriodPresentationModel";
import ShowSuccessMessageInteractor from "../../interactor/notifications/ShowSuccessMessageInteractor";
import GetReportingPeriodsInteractor from "../../interactor/reporting/GetReportingPeriodsInteractor";
import CreateReportingPeriodInteractor from "../../interactor/reporting/CreateReportingPeriodInteractor";
import UpdateReportingPeriodInteractor from "../../interactor/reporting/UpdateReportingPeriodInteractor";
import { IReportingType } from "../../model/reporting/ReportingType";
import AssignApplicantsToReportingPeriodInteractor from "../../interactor/reporting/AssignApplicantsToReportingPeriodInteractor";
import IsAllowedRandomSelectionInteractor from "../../interactor/reporting/IsAllowedRandomSelectionInteractor";
import ShowWarningMessageInteractor from "../../interactor/notifications/ShowWarningMessageInteractor";
import SelectForAdditionalCheckInteractor from "../../interactor/reporting/SelectForAdditionalCheckInteractor";
import { Moment } from "moment";
import { IAdditionalCheckDetails } from "../../model/reporting/AdditionalCheckDetails";
import ConfirmAdditionalCheckInteractor from "../../interactor/reporting/ConfirmAdditionalCheckInteractor";
import { RoleConstants } from "../../common/Constants";

export interface TRandomSelectionPresentationModel {
  percentageOfCandidates: number;
  periodFrom?: Date;
  periodTo?: Date;
  min?: number;
  max?: number;
}

export interface TReportingPeriodPresenter extends TLoadingAwarePresenter {
  reportingPeriods: TReportingPeriodPresentationModel[];
  applicantAssignationDialogVisible: boolean;
  reportingTypes: IReportingType[];
  rowData?: any;
  randomSelectionDialogVisible: boolean;
  currentReportingPeriod?: TReportingPeriodPresentationModel;
  percetageOfCandidates?: number;
  confirmRandomSelectionDialogVisible: boolean;
  confirmData?: IAdditionalCheckDetails;
  randomSelectionData?: TRandomSelectionPresentationModel;
}

export interface IReportingPeriodPresenter
  extends TReportingPeriodPresenter,
    TPresentable {
  load(
    reportingPeriods: IReportingPeriod[],
    reportingTypes: IReportingType[],
    percetageOfCandidates?: number
  ): void;
  getReportingPeriods(data: IReportingPeriod[]): void;
  createReportingPeriod(data: TReportingPeriodPresentationModel): void;
  updateReportingPeriod(data: TReportingPeriodPresentationModel): void;
  assignApplicantsToReportingPeriod(
    data: TReportingPeriodPresentationModel
  ): void;
  setRowData(rowData: any): void;
  showApplicantAssignationDialog(): void;
  hideApplicantAssignationDialog(): void;
  showReportingPeriodDetails(id: number): void;
  onChangeRandomSelectionDialogVisible(
    value: boolean,
    record?: TReportingPeriodPresentationModel
  ): void;
  selectForAdditionalCheck(data: any): void;
  onChangeConfirmModal(): void;
  updateRandomSelectionData(data: any): void;
  confirmAdditionalCheck(): void;
  canShowRandomSelection(): boolean;
}

const defaultState: TReportingPeriodPresenter = {
  reportingPeriods: [],
  reportingTypes: [],
  randomSelectionDialogVisible: false,
  rowData: undefined,
  applicantAssignationDialogVisible: false,
  currentReportingPeriod: undefined,
  percetageOfCandidates: undefined,
  confirmRandomSelectionDialogVisible: false,
  confirmData: undefined,
  randomSelectionData: undefined
};

function formatReportingPeriod(data: any, reportingPeriod?: IReportingPeriod) {
  let reportingPeriodRecord = reportingPeriod || ReportingPeriod(data);

  return reportingPeriodRecord;
}

const ReportingPeriodPresenter = withStore<
  IReportingPeriodPresenter,
  TReportingPeriodPresenter
>(({ application, store, loader, translate }): IReportingPeriodPresenter => {
  const _store = store;
  const _application: Application = application;
  const state = _store.getState<TReportingPeriodPresenter>();

  loader.start("reportingPeriodsLoader");

  const load = (
    reportingPeriods: IReportingPeriod[],
    reportingTypes: IReportingType[],
    percetageOfCandidates?: number
  ) => {
    _store.update({
      reportingPeriods: [
        ..._store.getState<TReportingPeriodPresenter>().reportingPeriods,
        ...reportingPeriods.map(ReportingPeriodPresentationModel)
      ],
      reportingTypes,
      percetageOfCandidates
    });
    loader.stop("reportingPeriodsLoader");
  };

  const getReportingPeriods = async () => {
    const reportingPeriods = await _application.container
      .resolve<GetReportingPeriodsInteractor>("getReportingPeriods")
      .execute();

    _store.update({
      reportingPeriods: [
        ...reportingPeriods.map(ReportingPeriodPresentationModel)
      ]
    });
  };

  const createReportingPeriod = async (
    data: TReportingPeriodPresentationModel
  ) => {
    try {
      const reportingPeriod = formatReportingPeriod(data);
      await _application.container
        .resolve<CreateReportingPeriodInteractor>("createReportingPeriod")
        .execute(reportingPeriod);

      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(
          translate(
            "reporting_periods.messages.create_reporting_period_success"
          )
        );
      await getReportingPeriods();
    } catch (error) {
      _application.container
        .resolve<ShowErrorMessageInteractor>("showErrorMessage")
        .execute((error as any).message);
    }
  };

  const updateReportingPeriod = async (
    data: TReportingPeriodPresentationModel
  ) => {
    try {
      const reportingPeriod = formatReportingPeriod(data);
      await _application.container
        .resolve<UpdateReportingPeriodInteractor>("updateReportingPeriod")
        .execute(reportingPeriod);

      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(
          translate(
            "reporting_periods.messages.update_reporting_period_success"
          )
        );
      await getReportingPeriods();
    } catch (error) {
      _application.container
        .resolve<ShowErrorMessageInteractor>("showErrorMessage")
        .execute((error as any).message);
    }
  };

  const assignApplicantsToReportingPeriod = async (
    data: TReportingPeriodPresentationModel
  ) => {
    try {
      const reportingPeriod = formatReportingPeriod(data);
      await _application.container
        .resolve<AssignApplicantsToReportingPeriodInteractor>(
          "assignApplicantsToReportingPeriod"
        )
        .execute(reportingPeriod);

      _application.container
        .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
        .execute(
          translate(
            "reporting_periods.messages.assign_applicant_to_reporting_period"
          )
        );
      await getReportingPeriods();
    } catch (error) {
      _application.container
        .resolve<ShowErrorMessageInteractor>("showErrorMessage")
        .execute((error as any).message);
    }
  };

  const setRowData = (rowData: any) => {
    _store.update({ rowData });
  };

  const showApplicantAssignationDialog = () => {
    _store.update({ applicantAssignationDialogVisible: true });
  };

  const hideApplicantAssignationDialog = () => {
    _store.update({ applicantAssignationDialogVisible: false });
  };

  const showReportingPeriodDetails = (id: number) => {
    _application.navigator.push({
      pathname: `/reporting-periods/${id}/details`
    });
  };

  const onChangeRandomSelectionDialogVisible = async (
    value: boolean,
    record?: TReportingPeriodPresentationModel
  ) => {
    _store.update({
      currentReportingPeriod: record
    });
    if (value) {
      const isAllowed =
        record &&
        (await _application.container
          .resolve<IsAllowedRandomSelectionInteractor>(
            "isAllowedRandomSelection"
          )
          .execute(record.id));
      if (isAllowed === false) {
        _application.container
          .resolve<ShowWarningMessageInteractor>("showWarningMessage")
          .execute(translate("random_selection.warning_msg"));
      } else {
        _store.update({
          randomSelectionDialogVisible: value
        });
      }
    } else {
      _store.update({
        randomSelectionDialogVisible: value
      });
      value === false && _store.update({ randomSelectionData: undefined });
    }
  };

  const selectForAdditionalCheck = async (data: any) => {
    loader.start("additionalCheckLoader");
    const currentReportingPeriod =
      _store.getState<TReportingPeriodPresenter>().currentReportingPeriod;
    let randomSelectionData = {
      percentageOfCandidates: data.percentageOfCandidates,
      periodFrom: data?.periodFrom
        ? (data.periodFrom as Moment).toDate()
        : undefined,
      periodTo: data?.periodTo ? (data.periodTo as Moment).toDate() : undefined,
      min: data.min,
      max: data.max
    } as TRandomSelectionPresentationModel;
    _store.update({ randomSelectionData });
    const confirmData = currentReportingPeriod
      ? await _application.container
          .resolve<SelectForAdditionalCheckInteractor>(
            "selectForAdditionalCheck"
          )
          .execute(
            currentReportingPeriod?.id,
            randomSelectionData.percentageOfCandidates,
            randomSelectionData.periodFrom,
            randomSelectionData.periodTo,
            randomSelectionData.min,
            randomSelectionData.max
          )
      : undefined;
    _store.update({
      confirmData
    });
    if (confirmData?.executionId) {
      _store.update({ confirmRandomSelectionDialogVisible: true });
    }
    loader.stop("additionalCheckLoader");
  };

  const onChangeConfirmModal = () => {
    const confirmRandomSelectionDialogVisible =
      _store.getState<TReportingPeriodPresenter>()
        .confirmRandomSelectionDialogVisible;
    _store.update({
      confirmRandomSelectionDialogVisible: !confirmRandomSelectionDialogVisible
    });
    if (
      !_store.getState<TReportingPeriodPresenter>()
        .confirmRandomSelectionDialogVisible
    ) {
      _store.update({ confirmData: undefined });
    }
  };

  const updateRandomSelectionData = (data: any) => {
    _store.update({
      randomSelectionData: data
    });
  };

  const confirmAdditionalCheck = async () => {
    loader.start("confirmAdditionalCheckLoader");
    const { currentReportingPeriod, confirmData } =
      _store.getState<TReportingPeriodPresenter>();
    currentReportingPeriod &&
      confirmData &&
      (await _application.container
        .resolve<ConfirmAdditionalCheckInteractor>("confirmAdditionalCheck")
        .execute(currentReportingPeriod.id, confirmData.executionId));
    _application.container
      .resolve<ShowSuccessMessageInteractor>("showSuccessMessage")
      .execute(translate("random_selection.success_msg"));
    _store.update({
      confirmRandomSelectionDialogVisible: false,
      randomSelectionDialogVisible: false,
      randomSelectionData: undefined,
      confirmData: undefined
    });
    loader.stop("confirmAdditionalCheckLoader");
  };

  const canShowRandomSelection = () => {
    const state: any = _store.getState();
    return state.roles.includes(RoleConstants.MIK_BO.MIK_OSOBLJE);
  };

  return {
    ...state,
    store: _store,
    loader,
    application: _application,
    translate,
    load,
    getReportingPeriods,
    createReportingPeriod,
    updateReportingPeriod,
    assignApplicantsToReportingPeriod,
    setRowData,
    showApplicantAssignationDialog,
    hideApplicantAssignationDialog,
    showReportingPeriodDetails,
    onChangeRandomSelectionDialogVisible,
    selectForAdditionalCheck,
    onChangeConfirmModal,
    updateRandomSelectionData,
    confirmAdditionalCheck,
    canShowRandomSelection
  };
}, defaultState);

export default ReportingPeriodPresenter;
